import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import paths from 'routes/paths';
import useEmails from 'hooks/emails/useEmails';
import useFormattedDate from 'hooks/useFormatDate';
import { Card as AntCard, Tag, Typography, Divider, Space, Row, Col, Avatar, Tooltip } from 'antd';
import { InfoCircleOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import CopyableText from "components/common/CopyableText";

const ViewSentEmailPage = () => {
  const { emailId } = useParams();
  const navigate = useNavigate();
  const { getSentEmailById, loading, error } = useEmails();
  const { formatDateTime } = useFormattedDate();
  const [email, setEmail] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [showNotFound, setShowNotFound] = useState(false);

  useEffect(() => {
    let isActive = true;
    const loadEmail = async () => {
      if (!emailId) return;
      setLocalError(null);
      setShowNotFound(false);
      try {
        const response = await getSentEmailById(emailId);
        if (isActive) setEmail(response || null);
      } catch (err) {
        if (isActive) setLocalError(err.message || 'Failed to load email');
      }
    };

    loadEmail();
    return () => {
      isActive = false;
    };
  }, [emailId, getSentEmailById]);

  useEffect(() => {
    if (loading || email || localError || error) {
      setShowNotFound(false);
      return;
    }
    const timer = setTimeout(() => setShowNotFound(true), 600);
    return () => clearTimeout(timer);
  }, [loading, email, localError, error]);

  if (loading && !email) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="text-muted mt-2">Loading email...</p>
        </div>
      </Container>
    );
  }

  if ((error || localError) && !email) {
    return (
      <Container fluid>
        <Alert variant="danger">{localError || error}</Alert>
        <Button variant="primary" onClick={() => navigate(paths.emailSent)}>
          Back
        </Button>
      </Container>
    );
  }

  if (!email) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="text-muted mt-2">Loading email...</p>
        </div>
      </Container>
    );
  }

  if (!email && showNotFound) {
    return (
      <Container fluid>
        <p className="text-muted">Email not found.</p>
        <Button variant="primary" onClick={() => navigate(paths.emailSent)}>
          Back
        </Button>
      </Container>
    );
  }



  const title = email.title ?? email.subject;
  const recipients = email.recipients ?? email.to ?? [];
  const body = email.body ?? email.content ?? '';
  const bodyHtml = email.bodyHtml ?? '';
  const status = email.status ?? email.emailStatus ?? 'SENT';
  const sentAt = formatDateTime(email.sentAt);
  const sender = email.sender ?? 'no-reply@oliveeb.com';
  const cc = email.cc ?? [];
  const bcc = email.bcc ?? [];
  const recipientsList = Array.isArray(recipients) ? recipients : [];
  const ccList = Array.isArray(cc) ? cc : [];
  const bccList = Array.isArray(bcc) ? bcc : [];

  const statusTag = (() => {
    const value = String(status || 'SENT').toUpperCase();
    const color =
      value === 'FAILED'
        ? 'error'
        : value === 'PENDING'
          ? 'warning'
          : value === 'PROCESSING'
            ? 'processing'
            : value === 'RECEIVED'
              ? 'default'
              : 'success';
    return <Tag color={color}>{value}</Tag>;
  })();

  const renderAddressList = (list) => (
    list.length ? (
      <Space size={[6, 6]} wrap>
        {list.map((item, index) => (
          <Tag key={`${String(item)}-${index}`}>{item}</Tag>
        ))}
      </Space>
    ) : <span className="text-muted">—</span>
  );

  return (
    <Container fluid>
      <div className="gmail-detail-shell">
        <div className="gmail-detail-header">
          <div className="gmail-detail-title">
            <Button variant="light" size="sm" onClick={() => navigate(paths.emailSent)} className="gmail-back-btn">
              <FontAwesomeIcon icon="arrow-left" />
            </Button>
            <div>
              <div className="gmail-detail-subtitle">Sent Email</div>
              <div className="gmail-detail-subject">{title || 'Untitled'}</div>
            </div>
          </div>
          <div className="gmail-detail-actions" />
        </div>

        <Row gutter={[16, 16]} className="gmail-detail-grid">
          <Col xs={24} lg={16}>
            <AntCard className="gmail-detail-card shadow-sm border-0">
              <div className="gmail-from-row">
                <Avatar size={40} icon={<UserOutlined />} />
                <div className="gmail-from-meta">
                  <div className="gmail-from-name">{sender}</div>
                  <div className="gmail-from-to">
                    <span className="text-muted">To</span> {renderAddressList(recipientsList)}
                  </div>
                </div>
                <div className="gmail-from-right">
                  <div className="gmail-time">
                    <ClockCircleOutlined className="me-2" />
                    {sentAt}
                  </div>
                  {statusTag}
                </div>
              </div>

              <Divider />

              <div className="gmail-address-grid">
                <div className="gmail-address-item">
                  <div className="gmail-label">From</div>
                  <div className="gmail-value">{sender}</div>
                </div>
                <div className="gmail-address-item">
                  <div className="gmail-label">To</div>
                  <div className="gmail-value">{renderAddressList(recipientsList)}</div>
                </div>
                <div className="gmail-address-item">
                  <div className="gmail-label">CC</div>
                  <div className="gmail-value">{renderAddressList(ccList)}</div>
                </div>
                <div className="gmail-address-item">
                  <div className="gmail-label">BCC</div>
                  <div className="gmail-value">{renderAddressList(bccList)}</div>
                </div>
              </div>

              <Divider />

              <div className="gmail-message">
                <Typography.Text type="secondary" className="text-uppercase small">
                  Message
                </Typography.Text>
                <div
                  className="gmail-message-body"
                  style={bodyHtml ? { whiteSpace: 'normal' } : undefined}
                >
                  {bodyHtml ? (
                    <div
                      className="gmail-rendered-html"
                      dangerouslySetInnerHTML={{ __html: bodyHtml }}
                    />
                  ) : (
                    body || 'No content'
                  )}
                </div>
              </div>
            </AntCard>
          </Col>
          <Col xs={24} lg={8}>
            <AntCard className="gmail-detail-side shadow-sm border-0">
              <div className="gmail-side-header">
                <InfoCircleOutlined />
                <span>Details</span>
              </div>
              <div className="gmail-side-row">
                <div className="gmail-label">Email ID</div>
                <div className="gmail-value">
                  <CopyableText value={email.id} displayValue={email.id ?? "—"} />
                </div>
              </div>
              <div className="gmail-side-row">
                <div className="gmail-label">Sent</div>
                <div className="gmail-value">{sentAt}</div>
              </div>
              <Divider />
              <div className="gmail-side-row">
                <div className="gmail-label">Status</div>
                <div className="gmail-value">
                  <Tooltip title="Delivery status">{statusTag}</Tooltip>
                </div>
              </div>
            </AntCard>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default ViewSentEmailPage;
